import { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ExitBadge } from '../components/Badges';
import { BookOpen, Plus, X, AlertTriangle, TrendingUp, TrendingDown } from 'lucide-react';

export default function RealTradesModule() {
  const { realTrades, addRealTrade, closeRealTrade, signals, settings } = useApp();
  const [showAdd, setShowAdd] = useState(false);
  const [showClose, setShowClose] = useState(null);
  const [form, setForm] = useState({ symbol: '', entryPrice: '', stopPrice: '', shares: '' });
  const [exitPrice, setExitPrice] = useState('');

  const openTrades = useMemo(() => realTrades.filter(t => t.status === 'OPEN'), [realTrades]);
  const closedTrades = useMemo(() => realTrades.filter(t => t.status === 'CLOSED'), [realTrades]);

  const totalExposure = openTrades.reduce((s, t) => s + (t.entryPrice * t.shares), 0);
  const totalPnL = closedTrades.reduce((s, t) => s + (t.pnl || 0), 0);
  const openPnL = openTrades.reduce((s, t) => {
    const signal = signals.find(sig => sig.symbol === t.symbol);
    const currentPrice = signal?.price || t.entryPrice;
    return s + (currentPrice - t.entryPrice) * t.shares;
  }, 0);

  const handleSubmitTrade = () => {
    const entryPrice = parseFloat(form.entryPrice);
    const stopPrice = parseFloat(form.stopPrice);
    if (!form.symbol || !entryPrice || !stopPrice) return;

    // Check averaging down rule
    const hasOpen = openTrades.some(t => t.symbol === form.symbol.toUpperCase());
    if (hasOpen) return;

    addRealTrade({
      symbol: form.symbol.toUpperCase(),
      entryPrice,
      stopPrice,
      shares: form.shares ? parseInt(form.shares) : undefined,
    });
    setForm({ symbol: '', entryPrice: '', stopPrice: '', shares: '' });
    setShowAdd(false);
  };

  const handleCloseTrade = () => {
    if (!showClose || !exitPrice) return;
    closeRealTrade(showClose, exitPrice);
    setShowClose(null);
    setExitPrice('');
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="real-trades-module">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-trading-blue" />
            Real Trades
          </h1>
          <p className="text-sm text-trading-muted mt-1">Manual trade tracking with risk management</p>
        </div>
        <Button onClick={() => setShowAdd(true)} data-testid="add-trade-btn" className="bg-trading-blue hover:bg-trading-blue/90 text-white rounded-sm">
          <Plus className="w-4 h-4 mr-2" /> New Trade
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <SummaryCard label="Open Positions" value={openTrades.length} max={settings.maxPositions} testId="open-positions-count" />
        <SummaryCard label="Exposure" value={`PHP ${totalExposure.toLocaleString()}`} testId="total-exposure" />
        <SummaryCard label="Open P&L" value={`${openPnL >= 0 ? '+' : ''}${openPnL.toLocaleString()}`} color={openPnL >= 0 ? 'text-trading-green' : 'text-trading-red'} testId="open-pnl" />
        <SummaryCard label="Realized P&L" value={`${totalPnL >= 0 ? '+' : ''}${totalPnL.toLocaleString()}`} color={totalPnL >= 0 ? 'text-trading-green' : 'text-trading-red'} testId="realized-pnl" />
      </div>

      {/* Risk warnings */}
      {openTrades.length >= settings.maxPositions && (
        <div className="mb-4 p-3 bg-trading-amber/5 border border-trading-amber/20 rounded-sm flex items-center gap-2 text-trading-amber text-sm">
          <AlertTriangle className="w-4 h-4" /> Maximum positions reached ({settings.maxPositions})
        </div>
      )}

      <Tabs defaultValue="open">
        <TabsList className="bg-trading-elevated border border-trading-border mb-4">
          <TabsTrigger value="open" className="data-[state=active]:bg-trading-surface text-trading-muted data-[state=active]:text-trading-text text-sm">
            Open ({openTrades.length})
          </TabsTrigger>
          <TabsTrigger value="closed" className="data-[state=active]:bg-trading-surface text-trading-muted data-[state=active]:text-trading-text text-sm">
            Closed ({closedTrades.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="open">
          <TradeTable
            trades={openTrades}
            type="open"
            signals={signals}
            onClose={(id) => { setShowClose(id); setExitPrice(''); }}
          />
        </TabsContent>
        <TabsContent value="closed">
          <TradeTable trades={closedTrades} type="closed" signals={signals} />
        </TabsContent>
      </Tabs>

      {/* Add Trade Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-trading-surface border-trading-border" data-testid="add-trade-dialog">
          <DialogHeader>
            <DialogTitle className="text-trading-text">New Trade</DialogTitle>
            <DialogDescription className="text-trading-muted text-sm">Enter trade details. Shares auto-calculated if empty.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-trading-muted font-semibold mb-1 block">Symbol</label>
              <Input
                data-testid="trade-symbol-input"
                value={form.symbol}
                onChange={e => setForm(p => ({ ...p, symbol: e.target.value }))}
                placeholder="e.g. JFC"
                className="bg-trading-bg border-trading-border text-trading-text h-8"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-trading-muted font-semibold mb-1 block">Entry Price</label>
                <Input
                  data-testid="trade-entry-input"
                  type="number"
                  step="0.01"
                  value={form.entryPrice}
                  onChange={e => setForm(p => ({ ...p, entryPrice: e.target.value }))}
                  placeholder="0.00"
                  className="bg-trading-bg border-trading-border text-trading-text h-8"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-trading-muted font-semibold mb-1 block">Stop Price</label>
                <Input
                  data-testid="trade-stop-input"
                  type="number"
                  step="0.01"
                  value={form.stopPrice}
                  onChange={e => setForm(p => ({ ...p, stopPrice: e.target.value }))}
                  placeholder="0.00"
                  className="bg-trading-bg border-trading-border text-trading-text h-8"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-trading-muted font-semibold mb-1 block">Shares (optional)</label>
              <Input
                data-testid="trade-shares-input"
                type="number"
                value={form.shares}
                onChange={e => setForm(p => ({ ...p, shares: e.target.value }))}
                placeholder="Auto-calculate from risk"
                className="bg-trading-bg border-trading-border text-trading-text h-8"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAdd(false)} className="text-trading-muted border border-trading-border rounded-sm">Cancel</Button>
            <Button onClick={handleSubmitTrade} data-testid="submit-trade-btn" className="bg-trading-blue hover:bg-trading-blue/90 text-white rounded-sm">
              Add Trade
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Trade Dialog */}
      <Dialog open={!!showClose} onOpenChange={() => setShowClose(null)}>
        <DialogContent className="bg-trading-surface border-trading-border" data-testid="close-trade-dialog">
          <DialogHeader>
            <DialogTitle className="text-trading-text">Close Trade</DialogTitle>
            <DialogDescription className="text-trading-muted text-sm">Enter exit price to close position.</DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-trading-muted font-semibold mb-1 block">Exit Price</label>
            <Input
              data-testid="exit-price-input"
              type="number"
              step="0.01"
              value={exitPrice}
              onChange={e => setExitPrice(e.target.value)}
              placeholder="0.00"
              className="bg-trading-bg border-trading-border text-trading-text h-8"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowClose(null)} className="text-trading-muted border border-trading-border rounded-sm">Cancel</Button>
            <Button onClick={handleCloseTrade} data-testid="confirm-close-btn" className="bg-trading-red hover:bg-trading-red/90 text-white rounded-sm">
              Close Position
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, value, max, color, testId }) {
  return (
    <div data-testid={testId} className="bg-trading-surface border border-trading-border rounded-sm p-3">
      <span className="text-[10px] uppercase tracking-widest text-trading-muted block mb-1">{label}</span>
      <span className={`font-mono text-lg tabular-nums ${color || 'text-trading-text'}`}>
        {value}
        {max !== undefined && <span className="text-xs text-trading-muted">/{max}</span>}
      </span>
    </div>
  );
}

function TradeTable({ trades, type, signals, onClose }) {
  if (!trades.length) {
    return (
      <div className="bg-trading-surface border border-trading-border rounded-sm p-8 text-center text-trading-muted text-sm">
        No {type} trades.
      </div>
    );
  }

  return (
    <div className="bg-trading-surface border border-trading-border rounded-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full data-table" data-testid={`${type}-trades-table`}>
          <thead>
            <tr className="bg-trading-elevated border-b border-trading-border">
              <th>Symbol</th>
              <th>Entry</th>
              <th>Stop</th>
              <th>Shares</th>
              <th>Exposure</th>
              {type === 'open' && <th>Current</th>}
              {type === 'open' && <th>Unrealized</th>}
              {type === 'open' && <th>Exit Signal</th>}
              {type === 'closed' && <th>Exit</th>}
              {type === 'closed' && <th>P&L</th>}
              <th>Date</th>
              {type === 'open' && <th></th>}
            </tr>
          </thead>
          <tbody>
            {trades.map(t => {
              const signal = signals.find(s => s.symbol === t.symbol);
              const currentPrice = signal?.price || t.entryPrice;
              const unrealized = (currentPrice - t.entryPrice) * t.shares;
              return (
                <tr key={t.id} className="border-b border-trading-border/50 hover:bg-trading-elevated/30" data-testid={`trade-row-${t.id}`}>
                  <td className="font-semibold text-trading-text">{t.symbol}</td>
                  <td>{t.entryPrice?.toFixed(2)}</td>
                  <td className="text-trading-red">{t.stopPrice?.toFixed(2)}</td>
                  <td>{t.shares?.toLocaleString()}</td>
                  <td>{(t.entryPrice * t.shares).toLocaleString()}</td>
                  {type === 'open' && <td className="text-trading-blue">{currentPrice?.toFixed(2)}</td>}
                  {type === 'open' && (
                    <td className={unrealized >= 0 ? 'text-trading-green' : 'text-trading-red'}>
                      {unrealized >= 0 ? '+' : ''}{unrealized.toLocaleString()}
                    </td>
                  )}
                  {type === 'open' && <td><ExitBadge exitMode={signal?.exitMode} /></td>}
                  {type === 'closed' && <td>{t.exitPrice?.toFixed(2)}</td>}
                  {type === 'closed' && (
                    <td className={t.pnl >= 0 ? 'text-trading-green' : 'text-trading-red'}>
                      {t.pnl >= 0 ? '+' : ''}{t.pnl?.toLocaleString()}
                    </td>
                  )}
                  <td className="text-trading-muted text-xs">{(type === 'open' ? t.openDate : t.closeDate)?.slice(0, 10)}</td>
                  {type === 'open' && (
                    <td>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onClose(t.id)}
                        data-testid={`close-trade-${t.id}`}
                        className="text-trading-red/70 hover:text-trading-red text-xs h-6 px-2"
                      >
                        Close
                      </Button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
